import { Injectable, TemplateRef, inject } from '@angular/core';
import { Validators } from '@angular/forms';

import { Observable, filter, map } from 'rxjs';

import { CamAddEventRequest } from '@camelot/calendar';
import {
  CamAbstractInput,
  InputChoices,
  InputLabel,
  InputPanel,
  InputTextBox,
  InputTimePicker,
  computeTimeRanges,
  extractTimeFromDate,
  formDateTimeSection,
} from '@camelot/form-model';
import { CamUserInputService, EUserFormFields } from '@camelot/user';
import { isNonNullable } from '@camelot/utils';

import { CalendarNote } from '../dto/note';
import { CalendarNoteCreationPayloadInput } from '../dto/post/calendar-note-creation-payload-input';
import { CalendarNoteModifierPayloadInput } from '../dto/post/calendar-note-modifier-payload-input';
import { CamNoteTypesService } from '../note-type.service';

export enum ECalendarNoteFormFields {
  start = 'start',
  end = 'end',
  noteTypeId = 'noteTypeId',
  title = 'title',
  isAllDay = 'isAllDay',
}

type OptionFields = {
  noLabel?: boolean;
  options$?: Observable<unknown>;
  visible$?: Observable<boolean>;
};

type Template = 'noteType';

@Injectable({
  providedIn: 'root',
})
export class CamCalendarNoteFormService {
  private _usersInputService = inject(CamUserInputService);
  private _noteTypeService = inject(CamNoteTypesService);

  public getEventForm(
    template: { [key in Partial<Template>]: TemplateRef<any> },
    data?: CamAddEventRequest
  ): CamAbstractInput[] {
    const panelClass = 'highlight-title space-between color-border-primary py-space-md';

    const titleInput = new InputTextBox({
      key: ECalendarNoteFormFields.title,
      label: 'calendar-notes.form.title',
      validators: [Validators.required],
      value: data?.title ?? '',
    });
    const userInput = this._usersInputService.getUserInput(data?.userId ?? '', {
      label: 'calendar-notes.form.assignee',
    });
    const noteTypeInput = new InputChoices({
      key: ECalendarNoteFormFields.noteTypeId,
      label: 'calendar-notes.form.note-type',
      multiple: false,
      withSearch: true,
      options: this._noteTypeService.fetchNoteTypes$().pipe(
        filter(isNonNullable),
        map(noteTypes =>
          noteTypes.map(noteType => ({
            id: noteType.id,
            name: noteType.denomination,
            data: noteType,
          }))
        )
      ),
      value: data?.noteTypeId ? [data?.noteTypeId] : [],
      choiceTemplate: template ? { one: template['noteType'] } : {},
      validators: [Validators.required],
    });

    return [
      new InputPanel({
        key: 'panel-spacer',
        label: '',
        containerClass: [panelClass],
        contentClass: 'row mb-space-md',
        children: [
          new InputPanel({
            key: 'panel-main',
            label: 'calendar-notes.form.title',
            containerClass: [],
            contentClass: '',
            children: [
              new InputPanel({
                key: 'panel-main-grid',
                label: '',
                containerClass: [],
                contentClass: 'row g-space-md',
                children: [titleInput, userInput, noteTypeInput],
              }),
            ],
          }),
          formDateTimeSection({ start: data?.startDate?.toString(), end: data?.endDate?.toString() }),
        ],
      }),
    ];
  }

  public getEditForm(data: CalendarNote, template: { [key in Template]: TemplateRef<any> } | null) {
    const panelClass = 'highlight-title space-between color-border-primary py-space-md';

    const userInput = this._usersInputService.getUserInput(data?.userId ?? '', {
      label: 'calendar-notes.form.assignee',
    });

    return [
      new InputPanel({
        key: 'panel-spacer',
        containerClass: [panelClass],
        contentClass: 'row mb-space-md one-half',
        children: [
          new InputPanel({
            key: 'panel-main',
            containerClass: [],
            contentClass: '',
            children: [
              new InputPanel({
                key: 'panel-main-grid',
                containerClass: [],
                contentClass: 'row g-space-md',
                children: [userInput, this.getFields(ECalendarNoteFormFields.noteTypeId, data, template)],
              }),
            ],
          }),
          formDateTimeSection({ start: data?.start, end: data?.end }, { noLabel: true, noMultiDayOption: true }),
        ],
      }),
    ];
  }

  public formatEventForm(data: any): CalendarNoteCreationPayloadInput[] {
    const title = data[ECalendarNoteFormFields.title] ?? '';
    const userId = data[EUserFormFields.user] ? data[EUserFormFields.user][0] : '';
    const noteTypeId = data[ECalendarNoteFormFields.noteTypeId] ? data[ECalendarNoteFormFields.noteTypeId][0] : '';
    const isAllDay = data[ECalendarNoteFormFields.isAllDay] ?? false;

    const ranges = computeTimeRanges(data);
    return ranges.map(({ start, end }) => ({
      start,
      end,
      isAllDay,
      title,
      userId,
      noteTypeId,
    }));
  }

  public formatUpdateForm(id: string, data: any): Partial<CalendarNoteModifierPayloadInput> {
    const ranges = computeTimeRanges(data);
    return {
      id: id,
      start: ranges[0].start ?? null,
      end: ranges[0].end ?? null,
      isAllDay: data[ECalendarNoteFormFields.isAllDay] ?? false,
      userId: data[EUserFormFields.user] ? data[EUserFormFields.user][0] : null,
      noteTypeId: data[ECalendarNoteFormFields.noteTypeId] ? data[ECalendarNoteFormFields.noteTypeId][0] : null,
    };
  }

  public getFields(
    fields: ECalendarNoteFormFields,
    calendarNote: CalendarNote | null,
    template: { [key in Template]: TemplateRef<any> } | null,
    options?: OptionFields
  ): CamAbstractInput {
    switch (fields) {
      case ECalendarNoteFormFields.start:
        return new InputTimePicker({
          key: ECalendarNoteFormFields.start,
          label: options?.noLabel === true ? '' : 'calendar-notes.form.startHour',
          validators: [Validators.required],
          value: extractTimeFromDate(calendarNote?.start ?? ''),
        });
      case ECalendarNoteFormFields.end:
        return new InputTimePicker({
          key: ECalendarNoteFormFields.end,
          label: options?.noLabel === true ? '' : 'calendar-notes.form.end',
          value: extractTimeFromDate(calendarNote?.end ?? ''),
          min: extractTimeFromDate(calendarNote?.start ?? ''),
          validators: [Validators.required],
        });
      case ECalendarNoteFormFields.noteTypeId:
        return new InputChoices({
          key: ECalendarNoteFormFields.noteTypeId,
          label: 'calendar-notes.form.note-type',
          multiple: false,
          withSearch: true,
          options: this._noteTypeService.fetchNoteTypes$().pipe(
            filter(isNonNullable),
            map(noteTypes =>
              noteTypes.map(noteType => ({
                id: noteType.id,
                name: noteType.denomination,
                data: noteType,
              }))
            )
          ),
          value: calendarNote?.noteType.id ? [calendarNote?.noteType.id] : [],
          choiceTemplate: template ? { one: template['noteType'] } : {},
          validators: [Validators.required],
        });
      default:
        return new InputLabel({
          key: 'nokey',
          label: '',
        });
    }
  }
}
